/**
 * RateLimitStats.jsx
 * Compact admin dashboard showing rate limit usage statistics
 */

import React, { useEffect, useMemo, useState } from "react";
import api from "../../shared/api/apiClient";
import { useTranslation } from "react-i18next";
import {
  BarChart3,
  ShieldAlert,
  CircleCheckBig,
  TrendingUp,
} from "lucide-react";
import "./RateLimitStats.css";

const formatErrorMessage = (err, fallback) => {
  const detail = err?.response?.data?.detail;

  if (!detail) return fallback;
  if (typeof detail === "string") return detail;

  if (Array.isArray(detail)) {
    return detail.map((d) => d?.msg || JSON.stringify(d)).join("; ");
  }

  if (typeof detail === "object") {
    return detail.msg || JSON.stringify(detail);
  }

  try {
    return String(detail);
  } catch {
    return fallback;
  }
};

const normalizeValue = (value) => {
  if (value === null || value === undefined) return "";
  return String(value).toLowerCase();
};

export function RateLimitStats() {
  const { t } = useTranslation();

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hours, setHours] = useState(24);
  const [accessDenied, setAccessDenied] = useState(false);

  const [endpointSearch, setEndpointSearch] = useState("");
  const [userSearch, setUserSearch] = useState("");

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hours]);

  const fetchStats = async () => {
    setLoading(true);

    try {
      const response = await api.get("/api/system/rate-limits/usage-summary", {
        params: { hours },
      });

      const data = response.data?.data ?? response.data;

      setStats(data);
      setError(null);
      setAccessDenied(false);
    } catch (err) {
      console.error("Error fetching stats:", err);

      if (err?.response?.status === 403) {
        setAccessDenied(true);
        setError(
          formatErrorMessage(
            err,
            t("error_fetching_rate_limit_statistics_access", {
              defaultValue:
                "Admin access to rate limit statistics is not enabled. Please contact SuperAdmin to grant permission.",
            })
          )
        );
      } else {
        setAccessDenied(false);
        setError(
          formatErrorMessage(
            err,
            t("error_fetching_rate_limit_statistics", {
              defaultValue: "Failed to load statistics",
            })
          )
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const totalRequests = stats?.total_requests || 0;
  const totalExceeded = stats?.total_exceeded || 0;
  const totalSuccessful = Math.max(0, totalRequests - totalExceeded);

  const exceedPercentage =
    totalRequests > 0
      ? ((totalExceeded / totalRequests) * 100).toFixed(2)
      : "0.00";

  const getSeverity = (percentage) => {
    if (percentage > 10) return "high";
    if (percentage > 5) return "medium";
    return "low";
  };

  const severity = getSeverity(Number(exceedPercentage));

  const endpointRows = useMemo(() => {
    const entries = Object.entries(stats?.by_endpoint || {});
    const q = endpointSearch.trim().toLowerCase();

    let rows = entries.map(([endpoint, data]) => {
      const requests = data?.requests || 0;
      const exceeded = data?.exceeded || 0;
      const pct =
        requests > 0 ? ((exceeded / requests) * 100).toFixed(2) : "0.00";

      return {
        endpoint,
        requests,
        exceeded,
        pct,
      };
    });

    if (q) {
      rows = rows.filter((row) =>
        [row.endpoint, row.requests, row.exceeded, row.pct].some((field) =>
          normalizeValue(field).includes(q)
        )
      );
    }

    rows.sort((a, b) => b.requests - a.requests);
    return rows;
  }, [stats, endpointSearch]);

  const userRows = useMemo(() => {
    const entries = Object.entries(stats?.by_user || {});
    const q = userSearch.trim().toLowerCase();

    let rows = entries.map(([userId, data]) => {
      const requests = data?.requests || 0;
      const exceeded = data?.exceeded || 0;
      const pct =
        requests > 0 ? ((exceeded / requests) * 100).toFixed(2) : "0.00";

      return {
        userId,
        requests,
        exceeded,
        pct,
      };
    });

    if (q) {
      rows = rows.filter((row) =>
        [row.userId, row.requests, row.exceeded, row.pct].some((field) =>
          normalizeValue(field).includes(q)
        )
      );
    }

    rows.sort((a, b) => b.requests - a.requests);
    return rows;
  }, [stats, userSearch]);

  const topEndpoint = useMemo(() => {
    const entries = Object.entries(stats?.by_endpoint || {});
    if (!entries.length) return null;

    return (
      entries
        .map(([endpoint, data]) => ({
          endpoint,
          requests: data?.requests || 0,
        }))
        .sort((a, b) => b.requests - a.requests)[0]?.endpoint || null
    );
  }, [stats]);

  const insights = useMemo(() => {
    const insightItems = [];

    if (totalRequests === 0) {
      insightItems.push({
        key: "no_requests",
        text: t("no_requests_in_period", {
          defaultValue: "No requests in this time period",
        }),
      });
    } else if (totalExceeded === 0) {
      insightItems.push({
        key: "no_violations",
        text: `${t("rate_limit_no_violations_prefix", {
          defaultValue: "✅",
        })} ${t("no_rate_limit_violations", {
          defaultValue: "No rate limit violations in this period",
        })}`,
      });
    } else if (Number(exceedPercentage) > 10) {
      insightItems.push({
        key: "high_exceed",
        text: `${t("rate_limit_high_exceed_prefix", {
          defaultValue: "⚠️",
        })} ${t("high_rate_limit_exceed_rate", {
          defaultValue: "High rate limit exceed rate",
        })} (${exceedPercentage}%)`,
      });
    } else {
      insightItems.push({
        key: "within_threshold",
        text: t("rate_limit_within_threshold", {
          defaultValue: "Rate limit usage is within acceptable threshold",
        }),
      });
    }

    if (topEndpoint) {
      insightItems.push({
        key: "top_endpoint",
        text: `${t("top_endpoint", {
          defaultValue: "Top endpoint",
        })}: ${topEndpoint}`,
      });
    }

    if (!insightItems.length) {
      insightItems.push({
        key: "no_insights_available",
        text: t("no_insights_available", {
          defaultValue: "No insights available for this period",
        }),
      });
    }

    return insightItems;
  }, [totalRequests, totalExceeded, exceedPercentage, topEndpoint, t]);

  return (
    <div className="container mt-3 rate-limit-page rate-limit-page-compact">
      <div className="d-flex justify-content-between align-items-center mb-2 gap-2 flex-wrap">
        <h2 className="mb-0">
          {t("rate_limit_statistics", {
            defaultValue: "Rate Limiting Statistics",
          })}
        </h2>

        <div className="d-flex gap-2 align-items-center flex-wrap">
          <select
            className="form-select form-select-sm"
            style={{ width: 130 }}
            value={hours}
            onChange={(e) => setHours(Number(e.target.value))}
            disabled={loading}
          >
            <option value={1}>
              {t("one_hour", { defaultValue: "1 hour" })}
            </option>
            <option value={6}>
              {t("six_hours", { defaultValue: "6 hours" })}
            </option>
            <option value={24}>
              {t("twenty_four_hours", { defaultValue: "24 hours" })}
            </option>
            <option value={168}>
              {t("seven_days", { defaultValue: "7 days" })}
            </option>
          </select>

          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={fetchStats}
            disabled={loading}
          >
            {loading
              ? `${t("loading", { defaultValue: "Loading" })}...`
              : t("refresh", { defaultValue: "Refresh" })}
          </button>
        </div>
      </div>

      {accessDenied && (
        <div className="alert alert-danger py-2 mb-3">
          <div className="d-flex align-items-start gap-2">
            <div className="rate-limit-alert-icon">
              {t("rate_limit_lock_icon", { defaultValue: "🔒" })}
            </div>
            <div>
              <div className="fw-semibold mb-1">
                {t("access_denied", { defaultValue: "Access denied" })}
              </div>
              <div>{error}</div>
            </div>
          </div>
        </div>
      )}

      {!accessDenied && error && (
        <div className="alert alert-danger py-2 mb-3">{error}</div>
      )}

      {!accessDenied && !error && (
        <>
          <div className="row g-2 mb-3">
            <div className="col-12 col-md-4">
              <div className="rate-summary-card compact h-100">
                <div className="rate-summary-title">
                  <BarChart3 className="rate-stat-icon" strokeWidth={2.1} />
                  <span>
                    {t("total_requests", { defaultValue: "Total Requests" })}
                  </span>
                </div>
                <div className="rate-summary-value">{totalRequests}</div>
              </div>
            </div>

            <div className="col-12 col-md-4">
              <div
                className={`rate-summary-card compact h-100 rate-summary-${severity}`}
              >
                <div className="rate-summary-title">
                  <ShieldAlert className="rate-stat-icon" strokeWidth={2.1} />
                  <span>
                    {t("rate_limited", { defaultValue: "Rate Limited" })}
                  </span>
                </div>
                <div className="rate-summary-value">{totalExceeded}</div>
                <div className="rate-summary-meta">{exceedPercentage}%</div>
              </div>
            </div>

            <div className="col-12 col-md-4">
              <div className="rate-summary-card compact h-100">
                <div className="rate-summary-title">
                  <CircleCheckBig className="rate-stat-icon" strokeWidth={2.1} />
                  <span>{t("successful", { defaultValue: "Successful" })}</span>
                </div>
                <div className="rate-summary-value">{totalSuccessful}</div>
              </div>
            </div>
          </div>

          <div className="rate-section compact mb-3">
            <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap px-2 py-2 border-bottom rate-section-head">
              <div>
                <h6 className="mb-0">
                  {t("by_endpoint", { defaultValue: "By Endpoint" })}
                </h6>
              </div>

              <div className="d-flex align-items-center gap-2 flex-wrap">
                <input
                  type="text"
                  className="form-control form-control-sm rate-search-input"
                  placeholder={t("search", { defaultValue: "Search" })}
                  value={endpointSearch}
                  onChange={(e) => setEndpointSearch(e.target.value)}
                />
                <div className="text-muted small">{endpointRows.length}</div>
              </div>
            </div>

            <div className="table-responsive rate-table-scroll">
              <table className="table table-striped table-hover align-middle mb-0 rate-table-compact">
                <thead>
                  <tr>
                    <th>{t("endpoint", { defaultValue: "Endpoint" })}</th>
                    <th>{t("requests", { defaultValue: "Requests" })}</th>
                    <th>{t("rate_limited", { defaultValue: "Rate Limited" })}</th>
                    <th>{t("percentage", { defaultValue: "Percentage" })}</th>
                  </tr>
                </thead>
                <tbody>
                  {endpointRows.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center py-3">
                        {t("no_data_available", {
                          defaultValue: "No data available",
                        })}
                      </td>
                    </tr>
                  ) : (
                    endpointRows.map((row) => (
                      <tr
                        key={row.endpoint}
                        className={Number(row.pct) > 5 ? "row-warning" : ""}
                      >
                        <td>
                          <code>{row.endpoint}</code>
                        </td>
                        <td>{row.requests}</td>
                        <td
                          className={
                            row.exceeded > 0 ? "text-danger fw-semibold" : ""
                          }
                        >
                          {row.exceeded}
                        </td>
                        <td>{row.pct}%</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="rate-section compact mb-3">
            <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap px-2 py-2 border-bottom rate-section-head">
              <div>
                <h6 className="mb-0">
                  {t("by_user", { defaultValue: "By User" })}
                </h6>
              </div>

              <div className="d-flex align-items-center gap-2 flex-wrap">
                <input
                  type="text"
                  className="form-control form-control-sm rate-search-input"
                  placeholder={t("search", { defaultValue: "Search" })}
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                />
                <div className="text-muted small">{userRows.length}</div>
              </div>
            </div>

            <div className="table-responsive rate-table-scroll">
              <table className="table table-striped table-hover align-middle mb-0 rate-table-compact">
                <thead>
                  <tr>
                    <th>{t("user_id", { defaultValue: "User ID" })}</th>
                    <th>{t("requests", { defaultValue: "Requests" })}</th>
                    <th>{t("rate_limited", { defaultValue: "Rate Limited" })}</th>
                    <th>{t("percentage", { defaultValue: "Percentage" })}</th>
                  </tr>
                </thead>
                <tbody>
                  {userRows.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center py-3">
                        {t("no_user_data_available", {
                          defaultValue: "No user data available",
                        })}
                      </td>
                    </tr>
                  ) : (
                    userRows.map((row) => (
                      <tr
                        key={row.userId}
                        className={Number(row.pct) > 5 ? "row-warning" : ""}
                      >
                        <td>#{row.userId}</td>
                        <td>{row.requests}</td>
                        <td
                          className={
                            row.exceeded > 0 ? "text-danger fw-semibold" : ""
                          }
                        >
                          {row.exceeded}
                        </td>
                        <td>{row.pct}%</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="rate-section compact">
            <div className="px-2 py-2 border-bottom rate-section-head">
              <h6 className="mb-0 rate-section-title-with-icon">
                <TrendingUp className="rate-section-icon" strokeWidth={2.1} />
                <span>{t("insights", { defaultValue: "Insights" })}</span>
              </h6>
            </div>

            <div className="rate-insights-list px-2 py-1">
              {insights.map((item) => (
                <div key={item.key} className="rate-insight-item">
                  {item.text}
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {loading && !stats && !error && !accessDenied && (
        <div className="rate-loading-state">
          {t("loading", { defaultValue: "Loading" })}...
        </div>
      )}
    </div>
  );
}

export default RateLimitStats;